import React, { useState, useEffect } from 'react';
import { getDevices, getPositions } from '../traccar';
import { odoMilesFromAttrs, isRentalVehicle, getActiveRental, getDoNotRent, addDoNotRent, removeDoNotRent } from '../rentalStore';
import RentalPanel from '../RentalPanel';

// Dedicated rental workspace. Pick a vehicle on the left, manage its rentals on
// the right. Everything rental-related lives here; the vehicle profile stays
// strictly for vehicle info + photos.
export default function RentalsPage() {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [devs, pos] = await Promise.all([getDevices(), getPositions()]);
      setDevices(devs); setPositions(pos); setError('');
    } catch (e) { setError(e.message || 'Could not load vehicles'); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const dName = (d) => (d.attributes || {}).displayName || d.name;
  const posFor = (id) => positions.find((p) => p.deviceId === id);
  const rentalDevices = devices.filter((d) => isRentalVehicle(d.id));
  const selected = rentalDevices.find((d) => d.id === openId);

  // Do Not Rent list
  const [dnrOpen, setDnrOpen] = useState(false);
  const [, dnrTick] = useState(0);
  const dnr = getDoNotRent();
  const [dnrForm, setDnrForm] = useState({ license: '', name: '', reason: '' });
  const addDnr = () => {
    if (!dnrForm.license.trim()) { alert('Enter a license number.'); return; }
    addDoNotRent(dnrForm);
    setDnrForm({ license: '', name: '', reason: '' }); dnrTick((n) => n + 1);
  };
  const inpS = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🚗 Rentals</h1>
        <p>{error ? `🔴 ${error}` : 'Manage every rental here — pricing, agreements, mileage, payments, calendar, history and reports. Pick a vehicle to begin.'}</p>
      </div>

      {/* Do Not Rent list */}
      <div style={{ background: 'white', border: '1px solid #fecaca', borderRadius: '12px', padding: '12px', marginBottom: '16px' }}>
        <div onClick={() => setDnrOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <span style={{ fontWeight: 800, fontSize: '14px', color: '#b91c1c', flex: 1 }}>🚫 Do Not Rent list {dnr.length > 0 && <span style={{ fontSize: '11px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '999px', padding: '1px 8px' }}>{dnr.length}</span>}</span>
          <span style={{ color: '#94a3b8' }}>{dnrOpen ? '▲' : '▼'}</span>
        </div>
        {dnrOpen && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Any rental whose renter's driver's license matches a number on this list is blocked from starting, with a warning.</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <input style={{ ...inpS, width: '150px' }} placeholder="License #" value={dnrForm.license} onChange={(e) => setDnrForm({ ...dnrForm, license: e.target.value })} />
              <input style={{ ...inpS, width: '160px' }} placeholder="Name (optional)" value={dnrForm.name} onChange={(e) => setDnrForm({ ...dnrForm, name: e.target.value })} />
              <input style={{ ...inpS, flex: 1, minWidth: '180px' }} placeholder="Reason (optional)" value={dnrForm.reason} onChange={(e) => setDnrForm({ ...dnrForm, reason: e.target.value })} />
              <button onClick={addDnr} style={{ background: '#b91c1c', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>＋ Add</button>
            </div>
            {dnr.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>No one on the list yet.</div>
            ) : dnr.map((e) => (
              <div key={e.license} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '7px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginBottom: '5px' }}>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>{e.license}</span>
                {e.name && <span style={{ color: '#334155' }}>· {e.name}</span>}
                {e.reason && <span style={{ color: '#94a3b8' }}>· {e.reason}</span>}
                <button onClick={() => { removeDoNotRent(e.license); dnrTick((n) => n + 1); }} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#b91c1c', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* vehicle list */}
        <div style={{ width: '260px', flexShrink: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Rental vehicles</div>
          {rentalDevices.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px' }}>No rental vehicles. Turn on “This vehicle is used for rentals” on a car’s profile (Vehicles tab) to manage it here.</div>}
          {rentalDevices.map((d) => {
            const on = isRentalVehicle(d.id);
            const active = getActiveRental(d.id);
            const isSel = openId === d.id;
            return (
              <button key={d.id} onClick={() => setOpenId(isSel ? null : d.id)} style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left',
                background: isSel ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : 'white',
                border: '1px solid ' + (isSel ? '#bfdbfe' : '#eef2f7'), borderRadius: '9px', padding: '8px 10px',
                marginBottom: '5px', cursor: 'pointer',
              }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', flex: 1 }}>{dName(d)}
                  {(d.attributes || {}).displayName && <span style={{ fontWeight: 500, color: '#94a3b8', fontSize: '10px' }}> · #{d.name}</span>}
                </span>
                {active ? <span style={{ fontSize: '9px', fontWeight: 800, padding: '2px 6px', borderRadius: '999px', background: '#dbeafe', color: '#1d4ed8' }}>ACTIVE</span>
                  : on ? <span style={{ fontSize: '9px', fontWeight: 800, padding: '2px 6px', borderRadius: '999px', background: '#dcfce7', color: '#15803d' }}>RENTAL</span>
                    : <span style={{ fontSize: '9px', fontWeight: 700, color: '#cbd5e1' }}>—</span>}
              </button>
            );
          })}
        </div>

        {/* rental panel for the selected vehicle */}
        <div style={{ flex: 1, minWidth: '320px' }}>
          {selected ? (() => {
            const p = posFor(selected.id);
            const a = (p && p.attributes) || {};
            const at = selected.attributes || {};
            return (
              <div>
                <div style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', marginBottom: '10px' }}>
                  {dName(selected)} {at.displayName && <span style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8' }}>#{selected.name}</span>}
                </div>
                <RentalPanel
                  deviceId={selected.id}
                  odometerMi={odoMilesFromAttrs(a)}
                  vehicle={{ name: dName(selected), year: at.vehYear, make: at.vehMake, model: at.vehModel, plate: at.vehPlate, vin: a.vin }}
                />
              </div>
            );
          })() : (
            <div style={{ background: 'white', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#64748b' }}>
              👈 Select a vehicle to manage its rentals.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
